import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

/**
 * XeroConnectionStatus Component
 *
 * Displays current Xero connection status with organisation details.
 * Shows connection state, organisation info, token expiry countdown, and last sync.
 * Includes reconnect button when disconnected or expired.
 *
 * @param {Object} props
 * @param {Object} props.connectionStatus - Connection status object from API
 * @param {boolean} props.isLoading - Whether status is being loaded
 * @param {Error} props.error - Error object if status check failed
 * @param {Function} props.onReconnect - Callback to trigger reconnection
 */
export default function XeroConnectionStatus({ connectionStatus, isLoading, error, onReconnect }) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <div className="w-4 h-4 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
        <span>Checking connection status...</span>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to check connection status. Please try again or contact support at{' '}
          <a href="mailto:support@caledoniamushrooms.com" className="underline">
            support@caledoniamushrooms.com
          </a>
        </AlertDescription>
      </Alert>
    )
  }

  if (!connectionStatus) {
    return null
  }

  const { connected, organisation, tokenExpiresAt, lastSyncAt } = connectionStatus

  if (!connected) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <XCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Not connected</span>
        </div>
        {onReconnect && (
          <Button
            onClick={onReconnect}
            size="sm"
            variant="outline"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reconnect
          </Button>
        )}
      </div>
    )
  }

  // Calculate token expiry status
  const tokenExpiryDate = tokenExpiresAt ? new Date(tokenExpiresAt) : null
  const isTokenExpired = tokenExpiryDate && tokenExpiryDate < new Date()

  // Access tokens expire every 30 minutes but refresh automatically
  // Only show informational message, not a warning
  const isAccessToken = tokenExpiryDate && (tokenExpiryDate - new Date()) < 31 * 60 * 1000 // < 31 minutes = access token

  // Refresh tokens expire after 60 days - this would require reconnection
  const isRefreshTokenExpiringSoon = tokenExpiryDate &&
    (tokenExpiryDate - new Date()) < 7 * 24 * 60 * 60 * 1000 && // < 7 days
    (tokenExpiryDate - new Date()) > 31 * 60 * 1000 // > 31 minutes (not an access token)

  return (
    <div className="space-y-3">
      {/* Connection Status Badge */}
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Connected
        </Badge>
      </div>

      {/* Organisation Details */}
      {organisation && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
            <span className="text-muted-foreground">Organisation:</span>
            <span className="font-medium">{organisation.name}</span>

            {organisation.shortCode && (
              <>
                <span className="text-muted-foreground">Short Code:</span>
                <span className="font-mono text-xs">{organisation.shortCode}</span>
              </>
            )}

            {organisation.type && (
              <>
                <span className="text-muted-foreground">Type:</span>
                <span>{organisation.type}</span>
              </>
            )}

            {organisation.countryCode && (
              <>
                <span className="text-muted-foreground">Country:</span>
                <span>{organisation.countryCode}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Token Expiry Information */}
      {isTokenExpired ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>Your Xero connection has expired. Please reconnect to continue syncing data.</span>
            {onReconnect && (
              <Button
                onClick={onReconnect}
                size="sm"
                variant="outline"
                className="gap-2 ml-4"
              >
                <RefreshCw className="h-4 w-4" />
                Reconnect
              </Button>
            )}
          </AlertDescription>
        </Alert>
      ) : isRefreshTokenExpiringSoon ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your Xero connection will expire{' '}
            {tokenExpiryDate && formatDistanceToNow(tokenExpiryDate, { addSuffix: true })}.
            Consider reconnecting soon to avoid disruption.
          </AlertDescription>
        </Alert>
      ) : isAccessToken && tokenExpiryDate ? (
        <p className="text-xs text-muted-foreground">
          Access token refreshes automatically {formatDistanceToNow(tokenExpiryDate, { addSuffix: true })}
        </p>
      ) : null}

      {/* Last Successful Sync */}
      {lastSyncAt && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Last successful sync:</span>
          <span className="font-medium">
            {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true })}
          </span>
        </div>
      )}
    </div>
  )
}
